import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ATRIBUTO_SIN_BARRA } from '#components/mobile/MobileNavTabs';
import { useCerrarConAtras } from '#components/mobile/ui/atras';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { BarraElegida, CatalogoInicio, SelectorTamano } from './EdicionInicio';
import {
  anadirWidget,
  cambiarTamanoVisible,
  CATALOGO_INICIO,
  moverVisible,
  quitarWidget,
  reordenarVisibles,
  TAMANOS_PERMITIDOS,
  tamanoVisible,
  widgetsDisponibles,
} from './modeloWidgets';
import type { Catalogo, IdWidget, WidgetInicio } from './modeloWidgets';
import { nombreWidget } from './widgets/registro';

/**
 * Personalizar una pantalla de tarjetas (Inicio o Informes) ahí mismo:
 * elegir, mover, cambiar el tamaño, quitar con «Deshacer» y añadir desde el
 * catálogo. Devuelve las props de la rejilla y las capas (barra de la
 * elegida, selector de tamaño y catálogo) que se pintan sobre la pantalla.
 */
export function useEdicionTarjetas({
  widgets,
  guardar,
  restablecer,
  noDisponibles,
  catalogo = CATALOGO_INICIO,
  pantalla = 'inicio',
}: {
  widgets: readonly WidgetInicio[];
  guardar: (lista: readonly WidgetInicio[]) => void;
  restablecer: () => void;
  noDisponibles: ReadonlySet<IdWidget>;
  catalogo?: Catalogo;
  pantalla?: 'inicio' | 'informes';
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editando, setEditando] = useState(false);
  const [elegida, setElegida] = useState<IdWidget | null>(null);
  const [tamanoDe, setTamanoDe] = useState<IdWidget | null>(null);
  // Catálogo abierto: `despues` es la pequeña junto a cuyo «+» se añade.
  const [abierto, setAbierto] = useState<{ despues: IdWidget | null } | null>(
    null,
  );
  const salirDeEditar = () => {
    setEditando(false);
    setElegida(null);
    setTamanoDe(null);
    setAbierto(null);
  };
  // «Atrás» del móvil termina de personalizar (no sale de la pantalla).
  useCerrarConAtras(editando, salirDeEditar);
  useCerrarConAtras(tamanoDe != null, () => setTamanoDe(null));

  // Al editar, la barra de pestañas baja y deja sitio.
  useEffect(() => {
    document.body.toggleAttribute(ATRIBUTO_SIN_BARRA, editando);
    return () => document.body.removeAttribute(ATRIBUTO_SIN_BARRA);
  }, [editando]);

  const visibles = widgets.filter(w => !noDisponibles.has(w.id));
  const idsVisibles = visibles.map(w => w.id);
  const disponibles = widgetsDisponibles(widgets, catalogo).filter(
    id => !noDisponibles.has(id),
  );

  const quitar = (id: IdWidget) => {
    const antes = widgets;
    guardar(quitarWidget(widgets, id));
    setElegida(null);
    setTamanoDe(null);
    dispatch(
      addNotification({
        notification: {
          type: 'message',
          message: t('{{name}} removed', { name: nombreWidget(t, id) }),
          timeout: 5000,
          button: { title: t('Undo'), action: () => guardar(antes) },
        },
      }),
    );
  };

  const anadir = (id: IdWidget) => {
    const despues = abierto?.despues;
    const i = despues ? widgets.findIndex(w => w.id === despues) : -1;
    // Donde quepa media, entra pequeña (junto al «+», a su lado); si no,
    // ancha.
    const lista = anadirWidget(
      widgets,
      id,
      'normal',
      i < 0 ? widgets.length : i + 1,
    );
    guardar(
      TAMANOS_PERMITIDOS[id].includes('pequena') && despues
        ? cambiarTamanoVisible(lista, id, 'pequena')
        : lista,
    );
    setAbierto(null);
  };

  const elegidaVisible =
    elegida && idsVisibles.includes(elegida) ? elegida : null;
  const widgetTamano = tamanoDe ? widgets.find(w => w.id === tamanoDe) : null;

  const rejilla = {
    widgets: visibles,
    editando,
    elegida: elegidaVisible,
    onEntrarEdicion: () => setEditando(true),
    onElegir: (id: IdWidget | null) => {
      setTamanoDe(null);
      setElegida(e => (id != null && e === id ? null : id));
    },
    onReordenar: (orden: IdWidget[]) =>
      guardar(reordenarVisibles(widgets, orden)),
    onQuitar: quitar,
    onTamano: (id: IdWidget) => {
      setElegida(null);
      setTamanoDe(id);
    },
    onAnadir: (despues: IdWidget) => setAbierto({ despues }),
  };

  const capas: ReactNode = (
    <>
      {editando && elegidaVisible && !tamanoDe && (
        <BarraElegida
          id={elegidaVisible}
          puedeSubir={idsVisibles.indexOf(elegidaVisible) > 0}
          puedeBajar={
            idsVisibles.indexOf(elegidaVisible) < idsVisibles.length - 1
          }
          onSubir={() =>
            guardar(moverVisible(widgets, idsVisibles, elegidaVisible, -1))
          }
          onBajar={() =>
            guardar(moverVisible(widgets, idsVisibles, elegidaVisible, 1))
          }
          onTamano={() => {
            setTamanoDe(elegidaVisible);
            setElegida(null);
          }}
          onQuitar={() => quitar(elegidaVisible)}
        />
      )}
      {editando && widgetTamano && (
        <SelectorTamano
          id={widgetTamano.id}
          actual={tamanoVisible(widgetTamano)}
          onElegir={tam => {
            guardar(cambiarTamanoVisible(widgets, widgetTamano.id, tam));
            setTamanoDe(null);
          }}
          onCerrar={() => setTamanoDe(null)}
        />
      )}
      {abierto && (
        <CatalogoInicio
          disponibles={disponibles}
          pantalla={pantalla}
          onAnadir={anadir}
          onRestablecer={() => {
            restablecer();
            setAbierto(null);
          }}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </>
  );

  return {
    editando,
    entrarEnEdicion: () => setEditando(true),
    salirDeEditar,
    visibles,
    abrirCatalogo: () => setAbierto({ despues: null }),
    rejilla,
    capas,
  };
}
